import { Box, Button, Text } from '@youversion/design-system-components';
import BankStroked from '@youversion/design-system-icons/lib/BankStroked';
import CardAmexStroked from '@youversion/design-system-icons/lib/CardAmexStroked';
import CardAppleStroked from '@youversion/design-system-icons/lib/CardAppleStroked';
import CardDiscoverStroked from '@youversion/design-system-icons/lib/CardDiscoverStroked';
import CardPaypalStroked from '@youversion/design-system-icons/lib/CardPaypalStroked';
import CardVisaStroked from '@youversion/design-system-icons/lib/CardVisaStroked';
import CreditCardStroked from '@youversion/design-system-icons/lib/CreditCardStroked';
import MasterCardStroked from '@youversion/design-system-icons/lib/MasterCardStroked';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { type JSX, useCallback } from 'react';
import { firebaseLogEvent } from 'utils/firebase';
import { useAmount } from 'utils/store/amount';
import { DEFAULT_FUND_ID, useFund } from 'utils/store/fund';

import { DonationRecord, PaymentMethod } from '@/graphql/history';

interface Props {
  item: DonationRecord;
  onClose: () => void;
}

const paymentIcons: { [key: string]: JSX.Element } = {
  ach: <BankStroked className='text-text-light dark:text-text-dark' />,
  'american express': <CardAmexStroked />,
  'apple-pay': <CardAppleStroked className='text-text-light dark:text-text-dark' />,
  bank: <BankStroked className='text-text-light dark:text-text-dark' />,
  credit: <CreditCardStroked className='text-text-light dark:text-text-dark' />,
  'credit-card': <CreditCardStroked className='text-text-light dark:text-text-dark' />,
  'credit-debit': <CreditCardStroked className='text-text-light dark:text-text-dark' />,
  'credit-generic': <CreditCardStroked className='text-text-light dark:text-text-dark' />,
  'diners-club': <CreditCardStroked className='text-text-light dark:text-text-dark' />,
  discover: <CardDiscoverStroked />,
  jcb: <CreditCardStroked className='text-text-light dark:text-text-dark' />,
  mastercard: <MasterCardStroked />,
  paypal: <CardPaypalStroked className='text-text-light dark:text-text-dark' />,
  unionpay: <CreditCardStroked className='text-text-light dark:text-text-dark' />,
  visa: <CardVisaStroked />,
};

const paymentMethodType = (paymentMethod: PaymentMethod) => {
  if (paymentMethod?.paymentMethodType?.toLowerCase() === 'paypal') {
    return paymentMethod.paymentMethodType;
  }
  return paymentMethod?.paymentType?.toLowerCase();
};

export function ListItem({ item, onClose }: Props) {
  const { setFund, getFundName, funds } = useFund();
  const { t } = useTranslation('common');
  const { setAmount } = useAmount();

  const handleGiveAgain = useCallback(
    async (amount: string, fundId: number) => {
      const fundIds = funds.map(fund => fund.id);
      const fundIsActive = fundIds.includes(fundId);
      await firebaseLogEvent('GivingButtonTap', { label: 'Give Again', screen: 'giving_history' });

      const newFundId = fundIsActive ? fundId : DEFAULT_FUND_ID;
      await setFund(newFundId);

      setAmount(amount);
      onClose();
    },
    [onClose, setAmount, setFund, funds]
  );

  return (
    <Box className='w-full flex space-i-2 justify-between' key={`giving-history-item-${item.id}`}>
      <Box className='flex space-i-2'>
        <Text className='text-text-muted-light dark:text-text-muted-dark' size='xs' variant='headline'>
          {format(new Date(item.paymentDate), 'LLL dd')}
        </Text>
        <Box className='space-b-1'>
          <Text size='xs' variant='headline'>
            {item.paymentAmount}
          </Text>
          <Box className='flex items-center space-i-0.5'>
            <Text className='text-16 text-text-muted-light dark:text-text-muted-dark'>{getFundName(item.fundId)}</Text>
            <Text className='text-16 text-text-muted-light dark:text-text-muted-dark'>•</Text>
            {paymentIcons[paymentMethodType(item.paymentMethod)] ? (
              <Box className='size-3'>{paymentIcons[paymentMethodType(item.paymentMethod)]}</Box>
            ) : (
              <Text>{paymentMethodType(item.paymentMethod)}</Text>
            )}
          </Box>
        </Box>
      </Box>
      <Button onClick={() => handleGiveAgain(item.paymentAmount, item.fundId)} size='sm' variant='secondary'>
        {t('giving.cta.give_again')}
      </Button>
    </Box>
  );
}
